import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  CheckCircle as PaidIcon,
  Schedule as PartialIcon,
  Undo as ReversedIcon,
  Block as VoidIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Shared Accounts Payable UI kit - mirrors receivables/ArShared.js exactly
// (same lifecycle, same visual vocabulary as every other ledger screen) so
// AP reads as one product with AR/GL/Cash rather than a bolt-on module.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'warning', Icon: PartialIcon },
  PAID: { label: 'Paid', color: 'success', Icon: PaidIcon },
  REVERSED: { label: 'Reversed', color: 'warning', Icon: ReversedIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const ApStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);
